import React, { useEffect, useMemo, useRef } from 'react';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import { Input as DropdownInput } from '@cobalt/react-dropdown';
import { Text } from '@cobalt/react-typography';
import Tag from '@cobalt/react-tag';
import Icon from '@cobalt/react-icon';

import { SelectorProps } from './index';
import TransIcon from '../TransIcon';

import { SelectOptionType } from '../types';

import SelectStore from '../hooks/useStore';

const INPUT_MAX_HEIGHT = '100px';

interface MultipleSelectorProps<T extends SelectOptionType = SelectOptionType> extends SelectorProps<T> {
  selectedValue: string[];
}

function MultipleSelector<T extends SelectOptionType = SelectOptionType>(props: MultipleSelectorProps<T>) {
  const {
    isOpen,
    inputRef,
    selectedValue,
    onToggleOpen,
    onClearSelect,
    onClearSelectedValue,

    options,
    placeholder,
    disabled,
    allowClear,
    loading,
    variation,
    size,
    maxTagCount,
    maxTagWidth = '90px',
    className,
    inputStyle,
    loadingText,
  } = props;

  const theme = useTheme();

  const { cacheRef } = SelectStore.useContainer();

  const prevLenRef = useRef(selectedValue.length);
  const scrollRef = useRef<HTMLDivElement>(null);

  const mergedStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      minHeight: '40px',
      '--input-height': 'auto',
      ...inputStyle,
    }),
    [inputStyle],
  );

  const tagStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      maxWidth: maxTagWidth,
      margin: '4px 4px 4px 0',
    }),
    [maxTagWidth],
  );

  const hiddenTagCount = useMemo(() => {
    return maxTagCount === Infinity ? 0 : selectedValue.length - maxTagCount!;
  }, [maxTagCount, selectedValue.length]);

  const handleClear = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    onClearSelect(value);
  };

  useEffect(() => {
    if (!scrollRef.current) return;

    if (prevLenRef.current > selectedValue.length) {
      prevLenRef.current = selectedValue.length;
      return;
    }
    prevLenRef.current = selectedValue.length;

    const scrollHeight = scrollRef.current.scrollHeight;
    scrollRef.current.scrollTo?.(0, scrollHeight);
  }, [selectedValue]);

  return (
    <DropdownInput
      data-testid="multipleSelector"
      forwardedRef={inputRef}
      disabled={disabled}
      variation={variation}
      size={size}
      className={className}
      style={mergedStyle}
      onClick={() => onToggleOpen()}
    >
      <Flex
        forwardedRef={scrollRef}
        style={{ flex: 1, padding: '2px 4px', maxHeight: INPUT_MAX_HEIGHT, scrollBehavior: 'smooth' }}
        scrollable
      >
        <Flex alignY="center" wrap>
          {/* Placeholder */}
          {selectedValue.length === 0 && (
            <Text size={size} truncated color={theme.basic500}>
              {!loading ? placeholder : loadingText}
            </Text>
          )}

          {/* Value */}
          {selectedValue.length > 0 && (
            <>
              {selectedValue.slice(0, maxTagCount).map((value) => {
                const selectedOption = options?.find((option) => option.value === value) || cacheRef.current.get(value);
                const selectedLabel =
                  selectedOption?.renderSelectedOption?.() || selectedOption?.renderOption?.() || selectedOption?.label;

                return (
                  <Tag
                    data-testid="selectorValue"
                    key={value}
                    style={tagStyle}
                    size={size}
                    title={selectedOption?.label}
                  >
                    {typeof selectedLabel === 'string' ? (
                      <Text size="small" truncated>
                        {selectedLabel}
                      </Text>
                    ) : (
                      selectedLabel
                    )}

                    {!disabled && (
                      <Box onClick={(e) => handleClear(e, value)}>
                        <Icon name="close" size="micro" />
                      </Box>
                    )}
                  </Tag>
                );
              })}

              {hiddenTagCount > 0 && <Tag color="theme">+{hiddenTagCount}</Tag>}
            </>
          )}
        </Flex>
      </Flex>

      <TransIcon
        disabled={disabled!}
        allowClear={allowClear!}
        loading={loading!}
        size={size}
        isOpen={isOpen}
        hasSelected={selectedValue.length > 0}
        onToggleOpen={onToggleOpen}
        onClearSelectedValue={onClearSelectedValue}
      />
    </DropdownInput>
  );
}

export default MultipleSelector;
